// src/context/PlatformAuthContext.js
//
// AuthContext.js's own Super Admin panel equivalent — same shape (token +
// permissions + loading, backed by localStorage, refetches permissions
// whenever the token changes) but deliberately its own separate context,
// its own separate localStorage keys ("platform_token"/"platformUser",
// never "token"/"user"), and no inactivity-timeout logic (that's a
// tenant-facing Config > Security setting — accounts.inactivity_timeout —
// read from a tenant's own ConfigManager; PlatformSetting has no
// equivalent, and the owner never asked for one for platform staff). This
// is what keeps a platform-staff session and a tenant session from ever
// colliding in the same browser — see services/api.js's own platformRequest
// doc comment for the matching backend-facing half of that same guarantee.
import { createContext, useState, useEffect, useCallback } from "react";
import {
  platformLogout as apiPlatformLogout,
  getPlatformUserPermissions,
} from "../services/api";

export const PlatformAuthContext = createContext();

export const PlatformAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem("platform_token"));
  const [platformUser, setPlatformUser] = useState(() => {
    try {
      const cached = localStorage.getItem("platformUser");
      if (cached && cached !== "undefined" && cached !== "null") {
        return JSON.parse(cached);
      }
    } catch (e) {
      console.error("Cached platform user is corrupted", e);
    }
    return null;
  });
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redirectAfterLogin, setRedirectAfterLogin] = useState(null);

  const logout = useCallback(async () => {
    try {
      const currentToken = localStorage.getItem("platform_token");
      if (currentToken) {
        await apiPlatformLogout(currentToken);
      }
    } catch (error) {
      console.error("Platform logout request failed:", error);
    } finally {
      localStorage.removeItem("platform_token");
      localStorage.removeItem("platformUser");
      setToken(null);
      setPlatformUser(null);
      setPermissions([]);
    }
  }, []);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!token) {
        setPermissions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const perms = await getPlatformUserPermissions(token);
        setPermissions(perms || []);
      } catch (err) {
        console.warn("Failed to fetch platform permissions:", err.message);
        logout();
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [token, logout]);

  const login = (tokenValue, user) => {
    localStorage.setItem("platform_token", tokenValue);
    if (user) localStorage.setItem("platformUser", JSON.stringify(user));
    setToken(tokenValue);
    setPlatformUser(user || null);
  };

  return (
    <PlatformAuthContext.Provider
      value={{
        token,
        platformUser,
        permissions,
        loading,
        login,
        logout,
        redirectAfterLogin,
        setRedirectAfterLogin,
      }}
    >
      {children}
    </PlatformAuthContext.Provider>
  );
};
